import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useClient } from "../../../../contexts/ClientContext";
import { getAccountLogo } from "../../../../shared/accountLogo";
import {
  EARTHQUAKE_HAZARD_COLOR_STOPS,
  FLOOD_HAZARD_COLOR_STOPS_Z13,
} from "../../constants";
import { captureAssessmentRiskSnapshots } from "./mapSnapshotHelpers";
import {
  buildAssessmentPdf,
  buildPdfFilename,
  loadImageAsDataUrl,
} from "./pdfHelpers";
import {
  isAreaAssessment,
  getAssessmentGeometry,
  getAreaTotals,
  getAreaPolygonMetrics,
} from "../../history/helpers";
import { formatArea, formatPerimeter } from "../../polygonDraw/helpers";

const EXPORT_SCOPE_CURRENT = "current-assessment";

const isValidNumber = (value) => Number.isFinite(Number(value));

const formatDate = (dateValue, locale) => {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(locale || "en", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatFilenameDate = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : new Date();
  if (Number.isNaN(date.getTime())) {
    const fallback = new Date();
    return `${String(fallback.getDate()).padStart(2, "0")}/${String(
      fallback.getMonth() + 1,
    ).padStart(2, "0")}/${fallback.getFullYear()}`;
  }

  return `${String(date.getDate()).padStart(2, "0")}/${String(
    date.getMonth() + 1,
  ).padStart(2, "0")}/${date.getFullYear()}`;
};

function normalizeAssessment({ assessment, address, creationTime }) {
  if (!assessment) return null;

  const latitude = Number(assessment.latitude);
  const longitude = Number(assessment.longitude);
  if (!isValidNumber(latitude) || !isValidNumber(longitude)) {
    return null;
  }

  const hasEgsa87 =
    assessment.egsa87x !== undefined &&
    assessment.egsa87x !== null &&
    assessment.egsa87x !== "" &&
    assessment.egsa87y !== undefined &&
    assessment.egsa87y !== null &&
    assessment.egsa87y !== "";
  const egsa87Address = hasEgsa87
    ? `${assessment.egsa87x}, ${assessment.egsa87y} (${latitude}, ${longitude})`
    : null;

  const createdAt = creationTime || assessment.creation_time || null;
  const isArea = isAreaAssessment(assessment);
  const geometry = isArea ? getAssessmentGeometry(assessment) : null;
  const totals = isArea ? getAreaTotals(assessment) : null;
  const polygonMetrics = isArea ? getAreaPolygonMetrics(assessment) : [];

  return {
    id: assessment.id || null,
    address:
      address ||
      egsa87Address ||
      assessment.address ||
      `${latitude}, ${longitude}`,
    latitude,
    longitude,
    floor: assessment.floor || null,
    name: assessment.name || null,
    notes: assessment.notes || null,
    creationTime: createdAt,
    isArea,
    geometry,
    areaSqm: totals?.area ?? null,
    perimeterM: totals?.perimeter ?? null,
    polygonCount: totals?.count ?? 0,
    polygonMetrics,
    risks: {
      fire: assessment.fire || {},
      flood: assessment.flood || {},
      earthquake: assessment.earthquake || {},
    },
  };
}

export function useAssessmentPdfExport() {
  const { t, i18n } = useTranslation();
  const { clientInfo } = useClient();
  const {
    logo,
    width: logoWidth,
    height: logoHeight,
  } = getAccountLogo(clientInfo?.account);
  const [activeExportKey, setActiveExportKey] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const mapboxToken = process.env.REACT_APP_MAPBOX_TOKEN;

  const labels = useMemo(
    () => ({
      reportTitle: t("risksMap.pdf.reportTitle"),
      generatedAt: t("risksMap.pdf.generatedAt", {
        value: formatDate(new Date(), i18n.resolvedLanguage || i18n.language),
      }),
      propertyDetails: t("risksMap.pdf.propertyDetails"),
      name: t("forms.name"),
      notes: t("forms.notes"),
      address: t("forms.address"),
      coordinates: t("forms.coordinates"),
      floor: t("forms.floor"),
      areaTitle: t("risksMap.polygon.areaTitle"),
      area: t("risksMap.pdf.area"),
      perimeter: t("risksMap.pdf.perimeter"),
      polygonCount: t("risksMap.pdf.polygonCount"),
      polygonLabel: t("risksMap.polygon.polygonLabel"),
      assessmentDate: t("common.date"),
      riskScore: t("risksMap.pdf.riskScore"),
      riskMessage: t("risksMap.pdf.riskMessage"),
      riskDescription: t("risksMap.pdf.riskDescription"),
      mapSnapshot: t("risksMap.pdf.mapSnapshot"),
      descriptionDictTranslate: (key, defaultValue, params) =>
        t(key, { defaultValue, ...params }),
      descriptionDictLocale: i18n.resolvedLanguage || i18n.language,
      riskTitle: (riskKey) => {
        if (riskKey === "fire") return t("portfolioMap.fireRiskSection");
        if (riskKey === "flood") return t("portfolioMap.floodRiskSection");
        return t("portfolioMap.earthquakeRiskSection");
      },
    }),
    [i18n.language, i18n.resolvedLanguage, t],
  );

  const exportAssessmentPdf = useCallback(
    async ({
      assessment,
      address,
      creationTime,
      mapStyle,
      earthquakeLayers,
      scopeKey = EXPORT_SCOPE_CURRENT,
    }) => {
      const normalized = normalizeAssessment({
        assessment,
        address,
        creationTime,
      });
      if (!normalized) {
        setErrorMessage(t("risksMap.pdf.errors.missingCoordinates"));
        return false;
      }

      if (!mapboxToken) {
        setErrorMessage(t("risksMap.pdf.errors.missingMapToken"));
        return false;
      }

      try {
        setErrorMessage("");
        setActiveExportKey(scopeKey);

        const [logoDataUrl, snapshots] = await Promise.all([
          loadImageAsDataUrl(logo),
          captureAssessmentRiskSnapshots({
            latitude: normalized.latitude,
            longitude: normalized.longitude,
            mapboxToken,
            mapStyle,
            geometry: normalized.geometry,
            fire: normalized.risks.fire,
            legendLabels: {
              fire: {
                title: t("risksMap.fireLegend.title"),
                labels: [
                  t("risksMap.fireLegend.woodyNeedleLeaved"),
                  t("risksMap.fireLegend.lowGrowingWoody"),
                  t("risksMap.fireLegend.permanentHerbaceous"),
                  t("risksMap.fireLegend.woodyBroadleavedEvergreen"),
                  t("risksMap.fireLegend.woodyBroadleavedDeciduous"),
                  t("risksMap.fireLegend.periodicallyHerbaceous"),
                  t("risksMap.fireLegend.lichensAndMosses"),
                  t("risksMap.fireLegend.sealed"),
                  t("risksMap.fireLegend.nonSparselyVegetated"),
                  t("risksMap.fireLegend.water"),
                  t("risksMap.fireLegend.snowAndIce"),
                ],
              },
              flood: {
                title: t("risksMap.floodLegend.title"),
                colors: FLOOD_HAZARD_COLOR_STOPS_Z13.map(([, color]) => color),
                labels: [
                  t("risksMap.hazardLegend.low"),
                  t("risksMap.hazardLegend.medium"),
                  t("risksMap.hazardLegend.elevated"),
                  t("risksMap.hazardLegend.high"),
                  t("risksMap.hazardLegend.very_high"),
                ],
              },
              earthquake: {
                title: t("risksMap.earthquakeLegend.title"),
                colors: EARTHQUAKE_HAZARD_COLOR_STOPS.map(([, color]) => color),
                labels: [
                  t("risksMap.hazardLegend.very_low"),
                  t("risksMap.hazardLegend.low"),
                  t("risksMap.hazardLegend.medium"),
                  t("risksMap.hazardLegend.high"),
                  t("risksMap.hazardLegend.very_high"),
                ],
                selectedLayers: {
                  hazard: t("risksMap.earthquakeOverlayOptions.hazard"),
                  tectonicPlates: t(
                    "risksMap.earthquakeOverlayOptions.tectonicPlates",
                  ),
                  activeFaults: t(
                    "risksMap.earthquakeOverlayOptions.activeFaults",
                  ),
                },
              },
            },
            earthquakeLayers,
          }),
        ]);

        const localizedAssessmentDateText = formatDate(
          normalized.creationTime,
          i18n.resolvedLanguage || i18n.language,
        );

        // Pre-format area/perimeter here (pdfHelpers has no `t`).
        const areaSection = normalized.isArea
          ? {
              count: normalized.polygonCount,
              areaText: formatArea(normalized.areaSqm, t),
              perimeterText: formatPerimeter(normalized.perimeterM, t),
              polygons: normalized.polygonMetrics.map((metric) => ({
                areaText: formatArea(metric.area, t),
                perimeterText: formatPerimeter(metric.perimeter, t),
              })),
            }
          : null;

        const pdf = await buildAssessmentPdf({
          assessment: {
            ...normalized,
            date: localizedAssessmentDateText,
            area: areaSection,
          },
          snapshots,
          labels: {
            ...labels,
            generatedAt: t("risksMap.pdf.generatedAt", {
              value: formatDate(
                new Date(),
                i18n.resolvedLanguage || i18n.language,
              ),
            }),
          },
          logoDataUrl,
          logoWidth,
          logoHeight,
        });

        pdf.save(
          buildPdfFilename({
            address: normalized.address,
            latitude: normalized.latitude,
            longitude: normalized.longitude,
            dateText: formatFilenameDate(normalized.creationTime),
          }),
        );
        return true;
      } catch (error) {
        console.error("Failed to export assessment PDF", error);
        setErrorMessage(t("risksMap.pdf.errors.exportFailed"));
        return false;
      } finally {
        setActiveExportKey(null);
      }
    },
    [
      i18n.language,
      i18n.resolvedLanguage,
      labels,
      logo,
      logoWidth,
      logoHeight,
      mapboxToken,
      t,
    ],
  );

  return {
    exportAssessmentPdf,
    activeExportKey,
    errorMessage,
    clearError: () => setErrorMessage(""),
    exportScopeCurrent: EXPORT_SCOPE_CURRENT,
  };
}
